import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiFetch, API } from "@/utils/api";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  // "loading" | "success" | "error"
  const [status, setStatus] = useState(token ? "loading" : "error");
  const [errorDetail, setErrorDetail] = useState(
    token ? "" : "Falta el token de verificación en el enlace."
  );

  // Reenvío del enlace caducado (roadmap 1.5): el usuario pide un token nuevo
  // desde aquí mismo, sin re-registrarse. Respuesta siempre neutra del backend.
  const [resendEmail, setResendEmail] = useState("");
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent | failed

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const verify = async () => {
      try {
        const response = await apiFetch(`${API}/auth/verify-email`, {
          method: "POST",
          body: JSON.stringify({ token }),
        });
        if (cancelled) return;
        if (response.ok) {
          setStatus("success");
        } else {
          const data = await response.json().catch(() => null);
          setErrorDetail(
            data?.detail || "El enlace de verificación no es válido o ha caducado."
          );
          setStatus("error");
        }
      } catch {
        if (!cancelled) {
          setErrorDetail("Error de conexión. Intenta abrir el enlace de nuevo.");
          setStatus("error");
        }
      }
    };
    verify();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResendState("sending");
    try {
      const response = await apiFetch(`${API}/auth/resend-verification`, {
        method: "POST",
        body: JSON.stringify({ email: resendEmail.trim() }),
      });
      setResendState(response.ok ? "sent" : "failed");
    } catch {
      setResendState("failed");
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-50 p-8"
      data-testid="verify-email-page"
    >
      <div className="w-full max-w-md text-center">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-8">
          LiquidArc
        </h1>

        {status === "loading" && (
          <div className="bg-white border border-slate-200 rounded-lg px-6 py-8" data-testid="verify-email-loading">
            <p className="text-slate-600">Verificando tu cuenta...</p>
          </div>
        )}

        {status === "success" && (
          <div className="bg-white border border-emerald-200 rounded-lg px-6 py-8 space-y-4" data-testid="verify-email-success">
            <h2 className="font-heading text-xl font-semibold text-slate-900">
              Cuenta verificada — ya puedes entrar
            </h2>
            <p className="text-sm text-slate-600">
              Tu email ha quedado confirmado. Inicia sesión para empezar.
            </p>
            <Button
              onClick={() => navigate("/login")}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium"
              data-testid="verify-email-login-btn"
            >
              Ir a iniciar sesión
            </Button>
          </div>
        )}

        {status === "error" && (
          <div className="bg-white border border-red-200 rounded-lg px-6 py-8 space-y-4" data-testid="verify-email-error">
            <h2 className="font-heading text-xl font-semibold text-slate-900">
              No se pudo verificar la cuenta
            </h2>
            <p className="text-sm text-red-600">{errorDetail}</p>

            {resendState === "sent" ? (
              <p
                className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
                data-testid="verify-email-resend-sent"
              >
                Si la cuenta existe y está pendiente de verificar, hemos enviado
                un nuevo enlace a ese email. Revisa tu bandeja de entrada (y la
                carpeta de spam).
              </p>
            ) : (
              <form onSubmit={handleResend} className="space-y-2 text-left">
                <p className="text-xs text-slate-500">
                  Si el enlace ha caducado, escribe tu email y te enviaremos uno
                  nuevo (caduca en 48 horas).
                </p>
                <Input
                  type="email"
                  required
                  placeholder="tu@email.com"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  data-testid="verify-email-resend-input"
                />
                <Button
                  type="submit"
                  disabled={resendState === "sending"}
                  className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white font-medium"
                  data-testid="verify-email-resend-btn"
                >
                  {resendState === "sending" ? "Enviando..." : "Enviar nuevo enlace"}
                </Button>
                {resendState === "failed" && (
                  <p className="text-xs text-red-600">
                    No se pudo pedir el enlace (¿demasiados intentos seguidos?).
                    Espera un minuto y vuelve a intentarlo.
                  </p>
                )}
              </form>
            )}

            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              className="w-full h-12 font-medium"
              data-testid="verify-email-back-btn"
            >
              Volver a iniciar sesión
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
